import { useMutation } from '@apollo/client';
import { Check, Close } from '@mui/icons-material';
import { Button, Dialog, DialogActions, DialogContent, TextField, Typography } from '@mui/material';
import * as Schema from 'generated/graphql/schema';
import React, { useCallback, useState } from 'react';
import { makeStyles } from 'tss-react/mui';

import DialogHeader from '@/components/dialogs/dialog-header';
import { upsertWiFi } from '@/graphql/mutations';
import { isWidthUp } from '@/hocs/with-width';
import { useTranslation, useURLQuery, useWidth } from '@/hooks';

const useStyles = makeStyles()((theme) => ({
  icon: {
    marginRight: theme.spacing(),
    marginTop: '-3px',
  },
}));

interface Properties {
  open: boolean;
  refetch: () => Promise<void>;
  handleOpenClose: (open: boolean) => void;
  initialState: Schema.NetworkInput | null;
}

const EditWiFiDialog: React.FunctionComponent<Properties> = (props) => {
  const { open, initialState, handleOpenClose, refetch } = props;

  const { softwareId = '' } = useURLQuery();
  const { classes } = useStyles();
  const [t] = useTranslation(['shared']);
  const width = useWidth();

  const [state, setState] = useState<Schema.NetworkInput>({
    id: initialState?.id || undefined,
    ssid: initialState?.ssid ?? '',
    password: initialState?.password ?? '',
  });

  const onWiFiSSIDChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
      const value = e.target.value.toString();
      setState({ ...state, ssid: value });
    },
    [state],
  );

  const onWiFiPassphraseChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
      const value = e.target.value.toString();
      setState({ ...state, password: value });
    },
    [state],
  );

  const [upsertWiFiMut] = useMutation<Schema.UpsertWiFiMutation, Schema.UpsertWiFiMutationVariables>(upsertWiFi);

  const discard = () => {
    handleOpenClose(false);
  };

  const save = useCallback(async () => {
    await upsertWiFiMut({
      variables: {
        uuid: softwareId,
        input: state,
      },
    });
    await refetch();
    handleOpenClose(false);
  }, [upsertWiFiMut, state, softwareId]);

  const title = initialState
    ? t(['shared:viewWifiConn'], { defaultValue: 'View WiFi Connection' })
    : t(['shared:addWifiConn'], { defaultValue: 'Add WiFi Connection' });

  return (
    <Dialog open={open} onClose={discard} scroll="body" fullWidth>
      <DialogHeader title={title} onClose={discard} />
      <DialogContent>
        <Typography variant="subtitle2">
          {t(['device:wifiCaseSensitive'], { defaultValue: 'Beware that both SSID and password is case-sensitive.' })}
        </Typography>
        <div style={{ display: 'flex', flexDirection: 'column', marginTop: '30px' }}>
          <TextField
            autoFocus
            type="text"
            name="ssid"
            label={t(['shared:ssid'], { defaultValue: 'SSID' })}
            onChange={onWiFiSSIDChange}
            variant="filled"
            style={{ marginBottom: '30px' }}
            value={state.ssid}
            disabled={!!initialState}
          />
          <TextField
            type="text"
            name="password"
            disabled={!!initialState}
            // FIXME: translation keys
            helperText={t(
              'device:passwordWillBeVisibleUntilNextPowerCycle',
              'The password will be visible until the next power cycle of the device',
            )}
            label={t(['shared:password'], { defaultValue: 'Password' })}
            onChange={onWiFiPassphraseChange}
            onFocus={() => setState({ ...state, password: '' })}
            style={{ marginBottom: '30px' }}
            variant="filled"
            value={state.password}
          />
        </div>
      </DialogContent>
      <DialogActions>
        <Button color="primary" data-skip onClick={discard}>
          <Close className={classes.icon} />
          {isWidthUp('md', width) ? t(['shared:cancel'], { defaultValue: 'Cancel' }) : ''}
        </Button>
        {!initialState && (
          <Button color="primary" data-skip onClick={save}>
            <Check className={classes.icon} />
            {isWidthUp('md', width) ? t(['shared:save'], { defaultValue: 'Save' }) : ''}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
export default EditWiFiDialog;
